import React, { useState } from 'react'
import MaterialTable from '@material-table/core'
import { TableCell } from '@mui/material'
import * as UIcons from '@iconscout/react-unicons'
import { withTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import useSWR, { mutate } from 'swr'
import TableActionBtn from 'components/TableActionBtn'
import StatusPill from '../../shared/StatusPill'
import { showModal, previewModal } from 'store/slices/miscSlice'
import AddBranch from './AddBranch'
import BranchPreview from './BranchPreview'
import EmptyBranch from './EmptyBranch'

export default withTranslation()(function BranchesTable({ t, q = '', location = '', status = '' }) {
  const dispatch = useDispatch()
  const { data } = useSWR('merchants/branches', { revalidateOnFocus: true })
  const { data: posRes } = useSWR('pos')
  const { data: contactsRes } = useSWR('merchants/contacts')
  const [editingBranch, setEditingBranch] = useState(null)
  const [selectedCount, setSelectedCount] = useState(0)

  const branches = data?.data ?? []
  const posDevices = posRes?.data ?? []
  const contacts = contactsRes?.data ?? []
  const contactNameById = contacts.reduce((acc, contact) => ({ ...acc, [contact.id]: `${contact.first_name ?? ''} ${contact.last_name ?? ''}`.trim() }), {})

  const filtered = branches.filter((branch) => {
    const query = q.trim().toLowerCase()
    if (query && !branch.name?.toLowerCase().includes(query)) return false
    if (location && branch.location !== location) return false
    if (status && (branch.status ?? 'active') !== status) return false
    return true
  })

  const openDetail = (branch) => dispatch(previewModal({
    title: t('misc.branch_details'),
    content: () => (
      <BranchPreview tableProps={{
        [t('settings.branch.table.header.name')]: branch.name,
        [t('settings.branch.table.header.location')]: branch.location,
        [t('settings.branch.table.header.manager')]: contactNameById[branch.branch_manager_id] ?? '—',
        [t('settings.branch.table.header.contact')]: branch.phone ?? '—',
        [t('settings.branch.form.address.label')]: branch.address ?? '—',
        [t('misc.status')]: branch.status ?? 'active'
      }}
      />
    )
  }))

  const openToggleStatus = (branch) => {
    const willDisable = branch.status !== 'inactive'
    dispatch(showModal({
      title: t(`settings.branch.${willDisable ? 'disable' : 'enable'}`),
      content: () => (
        <div className="text-md w-full text-center">
          {t('settings.branch.disable_confirm')}
        </div>
      ),
      url: `merchants/branches/${branch.id}`,
      method: 'put',
      extraPayload: { status: willDisable ? 'inactive' : 'active' },
      btnText: t(`settings.branch.${willDisable ? 'disable' : 'enable'}`),
      disableBtn: false,
      danger: willDisable,
      mutate: 'merchants/branches',
      successMsg: t(`settings.branch.success_${willDisable ? 'disable' : 'enable'}`)
    }))
  }

  const openDelete = (branch) => dispatch(showModal({
    title: t('settings.branch.delete'),
    content: () => (
      <div className="text-md w-full text-center">
        {t('settings.branch.delete_confirm')}
      </div>
    ),
    url: `merchants/branches/${branch.id}`,
    method: 'delete',
    btnText: t('settings.branch.delete'),
    disableBtn: false,
    danger: true,
    mutate: 'merchants/branches',
    successMsg: t('settings.branch.form.delete_success')
  }))

  if (!branches.length) return <EmptyBranch />

  return (
    <div className="w-full bg-white rounded-xl border border-border-lines overflow-hidden">
      <MaterialTable
        columns={[
          { title: t('settings.branch.table.header.name'), field: 'name' },
          { title: t('settings.branch.table.header.location'), field: 'location' },
          { title: t('settings.branch.table.header.manager'), field: 'branch_manager_id', render: (row) => contactNameById[row.branch_manager_id] ?? '—' },
          { title: t('settings.branch.table.header.contact'), field: 'phone', render: (row) => row.phone ?? '—' },
          {
            title: t('settings.branch.table.header.pos_devices'),
            field: 'id',
            render: (row) => posDevices.filter((device) => device.branch_id === row.id).length
          },
          { title: t('settings.branch.table.header.status'), field: 'status', render: (row) => <StatusPill active={(row.status ?? 'active') === 'active'} /> }
        ]}
        data={filtered}
        actions={[{ position: 'row', tooltip: t('settings.branch.view_details') }]}
        onSelectionChange={(rows) => setSelectedCount(rows.length)}
        options={{
          selection: true,
          actionsColumnIndex: -1,
          toolbar: false,
          pageSize: 10,
          pageSizeOptions: [10, 20, 50],
          headerStyle: { textTransform: 'uppercase', fontSize: '12px', color: '#748799', backgroundColor: '#F5F6FA' }
        }}
        localization={{
          header: { actions: '' },
          body: { emptyDataSourceMessage: t('misc.no_records') }
        }}
        components={{
          Pagination: (props) => {
            const totalPages = Math.max(1, Math.ceil(props.count / props.rowsPerPage))
            const currentPage = props.page + 1

            return (
              <TableCell colSpan={props.colSpan}>
                <div className="flex flex-wrap items-center justify-between gap-3 py-1">
                  <span className="text-sm text-secondary">
                    {t('settings.branch.table.selected', { count: selectedCount, total: props.count })}
                  </span>

                  <div className="flex items-center gap-4">
                    <select
                      value={props.rowsPerPage}
                      onChange={(event) => props.onRowsPerPageChange(event)}
                      className="border border-border-lines rounded-lg px-2 py-1 text-sm text-dark-blue"
                    >
                      {(props.rowsPerPageOptions ?? [10, 20, 50]).map((size) => <option key={size} value={size}>{size}</option>)}
                    </select>

                    <span className="text-sm text-secondary whitespace-nowrap">
                      {t('settings.branch.table.page_of', { page: currentPage, total: totalPages })}
                    </span>

                    <div className="flex items-center gap-1">
                      <button type="button" onClick={(event) => props.onPageChange(event, 0)} disabled={props.page === 0} className="p-1 rounded disabled:opacity-30">
                        <UIcons.UilAngleDoubleLeft size={16} className="text-secondary" />
                      </button>
                      <button type="button" onClick={(event) => props.onPageChange(event, props.page - 1)} disabled={props.page === 0} className="p-1 rounded disabled:opacity-30">
                        <UIcons.UilAngleLeft size={16} className="text-secondary" />
                      </button>
                      <button type="button" onClick={(event) => props.onPageChange(event, props.page + 1)} disabled={currentPage >= totalPages} className="p-1 rounded disabled:opacity-30">
                        <UIcons.UilAngleRight size={16} className="text-secondary" />
                      </button>
                      <button type="button" onClick={(event) => props.onPageChange(event, totalPages - 1)} disabled={currentPage >= totalPages} className="p-1 rounded disabled:opacity-30">
                        <UIcons.UilAngleDoubleRight size={16} className="text-secondary" />
                      </button>
                    </div>
                  </div>
                </div>
              </TableCell>
            )
          },

          Action: ({ data: row }) => (
            <TableActionBtn
              data={[
                {
                  Content: { icon: UIcons.UilInfoCircle, text: t('settings.branch.view_details') },
                  action: () => openDetail(row)
                },
                {
                  Content: { icon: UIcons.UilEditAlt, text: t('settings.branch.edit') },
                  action: () => setEditingBranch(row)
                },
                {
                  Content: { icon: UIcons.UilBan, text: t(`settings.branch.${row.status !== 'inactive' ? 'disable' : 'enable'}`) },
                  action: () => openToggleStatus(row)
                },
                {
                  Content: { icon: UIcons.UilTrash, text: t('settings.branch.delete'), classList: 'text-failure' },
                  action: () => openDelete(row)
                }
              ]}
            />
          )
        }}
      />

      {
        editingBranch && (
          <AddBranch
            branch={editingBranch}
            handleClick={() => setEditingBranch(null)}
            onSaved={() => mutate('merchants/branches')}
          />
        )
      }
    </div>
  )
})
